import type { CustomerSnapshotInput } from '../invoice';
import type { PaginationMeta } from '../journalEntry';
import type { Item } from '../item';

export type QuotationStatus = 'DRAFT' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'EXPIRED';

export const QUOTATION_STATUSES: QuotationStatus[] = [
  'DRAFT',
  'SENT',
  'ACCEPTED',
  'REJECTED',
  'EXPIRED',
];

export interface QuotationCustomerSnapshot {
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
}

export interface QuotationCustomerRef {
  id: number;
  display_name: string;
}

export interface QuotationItemSnapshot {
  name: string;
  sku: string;
  unit_price: string;
}

export interface QuotationLine {
  id: number;
  item_id: number;
  item_snapshot: QuotationItemSnapshot;
  line_number: number;
  description: string | null;
  quantity: string;
  unit_price: string;
  discount_amount: string;
  tax_amount: string;
  line_total: string;
  item?: Item | null;
}

export interface Quotation {
  id: number;
  customer_id: number;
  quotation_number: string;
  customer_snapshot: QuotationCustomerSnapshot;
  quote_date: string;
  valid_until: string | null;
  sub_total: string;
  discount_total: string;
  tax_total: string;
  total_amount: string;
  status: QuotationStatus;
  memo: string | null;
  notes: string | null;
  lines?: QuotationLine[];
  customer?: QuotationCustomerRef | null;
  created_at: string;
  updated_at: string;
}

export interface CreateQuotationLineData {
  item_id: number;
  description?: string | null;
  quantity: number;
  unit_price: number;
  discount_amount?: number;
  tax_amount?: number;
}

export interface CreateQuotationData {
  customer_id: number;
  customer_snapshot?: CustomerSnapshotInput;
  quote_date: string;
  valid_until?: string | null;
  status?: QuotationStatus;
  memo?: string | null;
  notes?: string | null;
  lines: CreateQuotationLineData[];
}

export interface UpdateQuotationData {
  customer_id?: number;
  customer_snapshot?: CustomerSnapshotInput;
  quote_date?: string;
  valid_until?: string | null;
  status?: QuotationStatus;
  memo?: string | null;
  notes?: string | null;
  lines?: CreateQuotationLineData[];
}

export interface CopyQuotationData {
  quote_date?: string;
  valid_until?: string | null;
  memo?: string | null;
}

export interface ConvertQuotationToInvoiceData {
  txn_date?: string;
  due_date?: string;
  memo?: string | null;
}

export interface SendQuotationData {
  to?: string;
  cc?: string | string[];
  subject?: string;
  message?: string;
}

export interface GetQuotationsParams {
  search?: string;
  customer_id?: number;
  status?: string;
  quote_date_from?: string;
  quote_date_to?: string;
  sort?: string;
  per_page?: number;
  page?: number;
}

export interface QuotationsResult {
  quotations: Quotation[];
  meta: PaginationMeta;
}

export interface QuotationsResponse {
  data: Quotation[];
  meta: PaginationMeta;
}

export interface QuotationResponse {
  data: Quotation;
}

export const getQuotationCustomerName = (quotation: Quotation): string =>
  quotation.customer?.display_name ?? quotation.customer_snapshot.name ?? '—';
